import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Newspaper, X } from 'lucide-react'

import { useSearchInvestigations } from '@/application/services/investigation.service'
import { Skeleton } from '@/ui/components/common/Skeleton'
import type { Investigation } from '@/domains/types'

export interface InvestigationSelection {
  id: string | null
  name: string
}

interface InvestigationSelectorProps {
  value: InvestigationSelection
  onChange: (investigation: InvestigationSelection) => void
}

/**
 * Combobox-style investigation picker: renders the currently-selected
 * investigation as a removable badge plus a search input with a results
 * dropdown that closes on outside-click. Mirrors `AuthorSelector`.
 */
export function InvestigationSelector({ value, onChange }: InvestigationSelectorProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: results, isLoading } = useSearchInvestigations(query)

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (investigation: Investigation) => {
    onChange({ id: investigation.id, name: investigation.name })
    setQuery('')
    setOpen(false)
  }

  const handleClear = () => {
    onChange({ id: null, name: '' })
    setQuery('')
  }

  return (
    <div className="space-y-2">
      {value.id ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm">
          <Newspaper className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium text-foreground flex-1 truncate">{value.name}</span>
          <button
            type="button"
            onClick={handleClear}
            className="p-0.5 rounded hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
            title={t('admin.form_investigation_clear')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic px-1">
          {t('admin.form_investigation_none')}
        </p>
      )}

      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setOpen(true)
            }}
            onFocus={() => query.length >= 2 && setOpen(true)}
            placeholder={t('admin.form_investigation_search_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
          />
        </div>

        {open && query.length >= 2 && (
          <div className="absolute top-full mt-1 left-0 right-0 bg-popover border border-border rounded-lg shadow-lg z-20 max-h-56 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-2 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : !results || results.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {t('admin.form_investigation_no_results')}
              </p>
            ) : (
              results.map((investigation) => (
                <button
                  key={investigation.id}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault()
                    handleSelect(investigation)
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent hover:text-accent-foreground text-left transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{investigation.name}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
